import React, { useState } from 'react'
import axios from 'axios'
import { Typography, IconButton } from '@material-ui/core'
import { Edit as EditIcon } from '@material-ui/icons'
import ComponentBuilder from '../LineItems/ComponentBuilder'

const COST_TYPE_STYLES = {
  labor: { bg: '#e8f5e9', color: '#2e7d32' },
  equipment: { bg: '#fff3e0', color: '#e65100' },
  material: { bg: '#e8eaf6', color: '#283593' },
  subcontract: { bg: '#e3f2fd', color: '#1565c0' },
  disposal: { bg: '#fce4ec', color: '#c62828' }
}

const fmt = (v) => '$' + (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })

const ComponentRecipeSummary = ({ projectService, project, url, onUpdate }) => {
  const [editIndex, setEditIndex] = useState(null)

  const components = (projectService.components || [])

  // Synthetic lineItem for ComponentBuilder
  const syntheticLineItem = {
    _id: projectService._id,
    quantity: projectService.totalQuantity || 0,
    unit: projectService.unit || ''
  }

  const handleSave = async (payload) => {
    // Update the component at editIndex in the projectService
    const updatedComponents = [...components]
    updatedComponents[editIndex] = { ...updatedComponents[editIndex], ...payload }

    try {
      await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}`, {
        components: updatedComponents
      })
      // Trigger recalculate
      await axios.post(`${url}/api/bid-estimation/project-services/${projectService._id}/recalculate`)
      setEditIndex(null)
      onUpdate()
    } catch (e) {
      console.error('Component save failed:', e)
    }
  }

  const totalCost = components.filter(c => c.included !== false).reduce((s, c) => s + (c.extendedCost || 0), 0)
  const totalSell = components.filter(c => c.included !== false).reduce((s, c) => s + (c.sellPrice || 0), 0)

  return (
    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Component Recipe</Typography>
        <Typography variant="caption" color="textSecondary">
          From template: <span style={{ color: '#1565c0' }}>{projectService.serviceId}</span>
        </Typography>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f5f5f5', color: '#666', fontSize: 10, textTransform: 'uppercase' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Role / Item</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Rate</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Hours</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Ext Cost</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Markup</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Sell</th>
            <th style={{ padding: '8px 12px', textAlign: 'center', width: 40 }}>Edit</th>
          </tr>
        </thead>
        <tbody>
          {components.map((c, i) => {
            const style = COST_TYPE_STYLES[c.costType] || {}
            const rate = c.productionRate?.hourlyRate || c.materialPricing?.unitCost || c.materialPricing?.manualOverride || c.subcontractPricing?.unitCost || 0
            const hours = c.productionRate?.hours
            const role = c.productionRate?.role || c.materialPricing?.selectedVendor || c.subcontractPricing?.subName || ''
            return (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{ backgroundColor: style.bg, color: style.color, padding: '1px 6px', borderRadius: 3, fontSize: 10 }}>
                    {c.costType}
                  </span>
                </td>
                <td style={{ padding: '8px 12px' }}>{c.description || '—'}</td>
                <td style={{ padding: '8px 12px', color: '#888', fontSize: 11 }}>{role || '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{rate ? `$${rate}` : '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{hours != null ? `${hours}h` : '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmt(c.extendedCost)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: '#888' }}>{c.markupPct ? `${c.markupPct}%` : '0%'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>{fmt(c.sellPrice)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <IconButton size="small" onClick={() => setEditIndex(i)}>
                    <EditIcon fontSize="small" style={{ color: '#1976d2', fontSize: 14 }} />
                  </IconButton>
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #e0e0e0', fontWeight: 700 }}>
            <td colSpan={5} style={{ padding: '10px 12px' }}>Totals ({projectService.totalQuantity || 0} {projectService.unit || ''})</td>
            <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmt(totalCost)}</td>
            <td style={{ padding: '10px 12px' }} />
            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#2e7d32' }}>{fmt(totalSell)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
      {projectService.totalQuantity > 0 && totalCost > 0 && (
        <div style={{ padding: '8px 16px', fontSize: 10, color: '#888', borderTop: '1px solid #f0f0f0' }}>
          Per-unit: {fmt(totalCost / projectService.totalQuantity)}/{projectService.unit || 'unit'} cost · {fmt(totalSell / projectService.totalQuantity)}/{projectService.unit || 'unit'} sell
        </div>
      )}

      {editIndex !== null && (
        <ComponentBuilder
          open={true}
          onClose={() => setEditIndex(null)}
          onSave={handleSave}
          lineItem={syntheticLineItem}
          project={project}
          url={url}
          editComponent={components[editIndex]}
          externalSave
        />
      )}
    </div>
  )
}

export default ComponentRecipeSummary
